// Evaluator for the `Condition` expressions used by `visibleWhen`,
// `requiredWhen` and `disabledWhen` in field schemas.
//
// Kept deliberately small and data-only: configs must never need to smuggle a
// function through JSON, because journey configs are meant to be serialisable
// (and, later, servable from the backend so a new product can ship without a
// frontend deploy).

/** @typedef {import("./types").Condition} Condition */

const isBlank = (v) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * Evaluate a condition against the current form values.
 * An absent condition means "always true", which is what makes `visibleWhen`
 * optional on every field.
 *
 * @param {Condition | undefined | null} condition
 * @param {Record<string, *>} values
 * @returns {boolean}
 */
export const evaluate = (condition, values) => {
  if (!condition) return true;

  if (Array.isArray(condition.and)) {
    return condition.and.every((c) => evaluate(c, values));
  }
  if (Array.isArray(condition.or)) {
    return condition.or.some((c) => evaluate(c, values));
  }
  if (condition.not) {
    return !evaluate(condition.not, values);
  }

  if (!condition.field) return true;
  const value = values?.[condition.field];

  if ("equals" in condition) return value === condition.equals;
  if ("notEquals" in condition) return value !== condition.notEquals;
  if (Array.isArray(condition.in)) return condition.in.includes(value);
  if ("truthy" in condition) return condition.truthy ? !isBlank(value) : isBlank(value);

  return true;
};

export { isBlank };
